import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  keycloak,
  initKeycloak,
  getUserInfo,
  hasRole,
  hasAnyRole,
  hasAllRoles,
} from '../services/keycloak';
import type { User, AuthContextType } from '../types/auth';
import { AuthContext } from './AuthContextDefinition';

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const authenticated = await initKeycloak();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const userInfo = getUserInfo();
          setUser(userInfo);
        }
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Set up token refresh
    const tokenRefreshInterval = setInterval(async () => {
      if (keycloak.authenticated) {
        try {
          await keycloak.updateToken(30);
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails, user needs to re-authenticate
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    }, 60000); // Check every minute

    // Listen for Keycloak events
    keycloak.onAuthSuccess = () => {
      setIsAuthenticated(true);
      const userInfo = getUserInfo();
      setUser(userInfo);

      // Handle redirect after successful authentication
      const storedRedirect = sessionStorage.getItem('loginRedirect');
      if (storedRedirect && storedRedirect !== window.location.pathname) {
        sessionStorage.removeItem('loginRedirect');
        // Use history.pushState instead of window.location to avoid page reload
        window.history.pushState({}, '', storedRedirect);
        // Dispatch a popstate event to trigger React Router navigation
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    };

    keycloak.onAuthError = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    keycloak.onAuthLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    keycloak.onTokenExpired = () => {
      // Try to refresh token
      keycloak.updateToken(30).catch(() => {
        setIsAuthenticated(false);
        setUser(null);
      });
    };

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  const login = () => {
    // Store current path for redirect after login, but only if it's not already an auth callback
    const currentPath = window.location.pathname + window.location.search;
    const isAuthCallback =
      currentPath.includes('state=') || currentPath.includes('code=');

    if (!isAuthCallback) {
      sessionStorage.setItem('loginRedirect', currentPath);
    }

    keycloak.login({
      redirectUri: window.location.origin,
    });
  };

  const logout = () => {
    keycloak.logout({
      redirectUri: window.location.origin,
    });
  };

  const register = () => {
    keycloak.register({
      redirectUri: window.location.origin,
    });
  };

  const checkRole = (role: string) => hasRole(role);
  const checkAnyRole = (roles: string[]) => hasAnyRole(roles);
  const checkAllRoles = (roles: string[]) => hasAllRoles(roles);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    register,
    hasRole: checkRole,
    hasAnyRole: checkAnyRole,
    hasAllRoles: checkAllRoles,
    token: keycloak.token || null,
  };

  // Block ALL children from mounting until Keycloak has finished its init check.
  // This prevents any RTK Query hooks from firing before the token is available.
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'var(--bg-primary)',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            border: '3px solid transparent',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
